'use client'

/**
 * src/components/DeleteAccountSection.tsx
 *
 * Settings → "Danger zone" — typed-confirmation flow for account
 * deletion. POSTs to /api/me/delete with the literal "DELETE"
 * confirmation token in the body. Server-side guarantees:
 *
 *   - cancel Stripe subscription if any
 *     (NOT an App Store subscription — Apple bills that, and only the
 *     student can cancel it. When they have one, both states below say so
 *     plainly, because deleting the account and being billed again next month
 *     is the worst outcome this screen can produce.)
 *   - hard-delete tutor history + student profile
 *   - anonymize User row (email, name, image, password, pseudonym)
 *   - set deletedAt → JWT callback refuses to issue further sessions
 *
 * On success, sign the user out + redirect to "/". The cookie still
 * lives until expiry but is now useless because the JWT callback
 * empties the session.user payload for deleted accounts.
 */

import { useState } from 'react'
import { signOut } from 'next-auth/react'
import { Button } from '@/components/ui/button'

const REQUIRED = 'DELETE'

function AppStoreWarning({ manageUrl }: { manageUrl: string }) {
  return (
    <p
      className="mt-2 rounded-md border border-amber-500/40 bg-amber-50/60 p-2 text-sm text-foreground dark:bg-amber-950/30"
      data-testid="delete-app-store-warning"
    >
      <span className="font-medium">Your App Store subscription won&apos;t be cancelled.</span>{' '}
      Apple bills it, and deleting your account doesn&apos;t stop it. Cancel it first on your iPhone
      (Settings → your name → Subscriptions) or{' '}
      <a
        href={manageUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="underline underline-offset-2"
      >
        on the App Store
      </a>
      .
    </p>
  )
}

export default function DeleteAccountSection({
  appStoreSubscription,
  appStoreManageUrl,
}: {
  /**
   * True when Apple is billing this account for Pro (we can't cancel that).
   * REQUIRED, not defaulted to false: a caller that forgot it would tell an
   * App Store subscriber their subscription "will be cancelled" — and Apple
   * would bill them next month for an account that no longer exists.
   */
  appStoreSubscription: boolean
  appStoreManageUrl: string
}) {
  const [open, setOpen] = useState(false)
  const [confirmText, setConfirmText] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const canSubmit = confirmText === REQUIRED && !submitting

  async function onSubmit() {
    if (!canSubmit) return
    setSubmitting(true)
    setError(null)
    try {
      const res = await fetch('/api/me/delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        cache: 'no-store',
        body: JSON.stringify({ confirm: REQUIRED }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        setError(
          typeof data?.message === 'string'
            ? data.message
            : typeof data?.error === 'string'
              ? data.error
              : 'Could not delete your account. Please try again or contact support.',
        )
        setSubmitting(false)
        return
      }
      // Account deleted server-side. Force sign-out and bounce home.
      await signOut({ callbackUrl: '/', redirect: true })
    } catch {
      setError('Network error. Please try again.')
      setSubmitting(false)
    }
  }

  if (!open) {
    return (
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-foreground">Delete my account</div>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Permanently removes your tutor history, study profile, and personal info.{' '}
            {appStoreSubscription
              ? 'A subscription on this website will be cancelled.'
              : 'Your active subscription will be cancelled.'}{' '}
            This cannot be undone.
          </p>
          {appStoreSubscription && <AppStoreWarning manageUrl={appStoreManageUrl} />}
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={() => setOpen(true)}
          className="shrink-0 border-rose-500/40 text-rose-700 hover:bg-rose-500/10 dark:text-rose-300"
        >
          Delete account
        </Button>
      </div>
    )
  }

  return (
    <div className="rounded-md border border-rose-500/30 bg-rose-50/40 p-4 dark:bg-rose-950/20">
      <div className="text-sm font-medium text-foreground">Are you sure?</div>
      <p id="delete-account-explainer" className="mt-1 text-sm text-muted-foreground">
        This will{' '}
        {appStoreSubscription
          ? 'cancel any subscription on this website'
          : 'cancel your subscription'}
        , erase your tutor chat history and student profile, and anonymize your account. Type{' '}
        <span className="font-mono font-semibold text-foreground">{REQUIRED}</span> in the box to
        confirm.
      </p>
      {appStoreSubscription && <AppStoreWarning manageUrl={appStoreManageUrl} />}

      <div className="mt-3 flex flex-col gap-2 sm:flex-row">
        <input
          type="text"
          aria-label={`Type ${REQUIRED} to confirm account deletion`}
          aria-describedby="delete-account-explainer"
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          placeholder={`Type ${REQUIRED} to confirm`}
          autoComplete="off"
          spellCheck={false}
          className="flex-1 rounded-md border border-border bg-background px-3 py-1.5 text-sm font-mono focus:border-rose-500 focus:outline-none focus:ring-1 focus:ring-rose-500"
        />
        <Button
          type="button"
          onClick={onSubmit}
          disabled={!canSubmit}
          className="bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-60"
        >
          {submitting ? 'Deleting…' : 'Permanently delete'}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            setOpen(false)
            setConfirmText('')
            setError(null)
          }}
          disabled={submitting}
        >
          Cancel
        </Button>
      </div>
      {error && <p className="mt-2 text-sm text-rose-700 dark:text-rose-300">{error}</p>}
    </div>
  )
}
